import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";

const RULES = [
  { key: "minLength", test: (pw, min) => pw.length >= min },
  { key: "uppercase", test: (pw) => /[A-Z]/.test(pw) },
  { key: "lowercase", test: (pw) => /[a-z]/.test(pw) },
  { key: "number", test: (pw) => /[0-9]/.test(pw) },
  { key: "special", test: (pw) => /[!@#$%^&*()\-_=+\[\]{};:'",.<>?/\\|`~]/.test(pw) },
];

export function PasswordStrength({ password = "", minLength = 10 }) {
  const { t } = useTranslation();

  const results = RULES.map((rule) => ({
    key: rule.key,
    passed: rule.test(password, minLength),
    label: rule.key === "minLength"
      ? t("passwordPolicy.minLength", { count: minLength })
      : t(`passwordPolicy.${rule.key}`),
  }));

  const passedCount = results.filter((r) => r.passed).length;
  const total = results.length;
  const strength = passedCount / total;

  const getStrengthColor = () => {
    if (strength <= 0.4) return "bg-red-500";
    if (strength <= 0.7) return "bg-amber-500";
    return "bg-emerald-500";
  };

  const getStrengthLabel = () => {
    if (password.length === 0) return "";
    if (strength <= 0.4) return t("passwordPolicy.weak");
    if (strength <= 0.7) return t("passwordPolicy.medium");
    return t("passwordPolicy.strong");
  };

  if (!password) return null;

  return (
    <div className="mt-2 space-y-2" data-testid="password-strength">
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${getStrengthColor()}`}
            style={{ width: `${strength * 100}%` }}
          />
        </div>
        <span className={`text-xs font-medium ${
          strength <= 0.4 ? "text-red-600" : strength <= 0.7 ? "text-amber-600" : "text-emerald-600"
        }`}>
          {getStrengthLabel()}
        </span>
      </div>

      {/* Rules checklist */}
      <ul className="space-y-0.5">
        {results.map((r) => (
          <li key={r.key} className="flex items-center gap-1.5 text-xs">
            {r.passed ? (
              <Check className="h-3 w-3 text-emerald-500 shrink-0" />
            ) : (
              <X className="h-3 w-3 text-slate-400 shrink-0" />
            )}
            <span className={r.passed ? "text-emerald-600" : "text-slate-500"}>
              {r.label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
